import { CapabilityButton } from "../CapabilityButton";
import { permissionNames as P } from "../../auth/permissions.generated";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Pencil, Trash2 } from "lucide-react";
import { useApiService } from "../../services/apiService";
import type { NavigationItem } from "../Navigation";
import type { Concept } from "../../types/concept";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";

interface ConceptDetailProps {
  conceptId: number;
  onNavigate: (view: NavigationItem, campaignId?: string, conceptId?: number) => void;
}

const DetailRow = ({ label, value }: { label: string; value?: string | null }) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
    <span className="text-sm text-foreground whitespace-pre-line">
      {value && value.trim().length > 0 ? value : "—"}
    </span>
  </div>
);

const formatDateTime = (value: string) => {
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

export function ConceptDetail({ conceptId, onNavigate }: ConceptDetailProps) {
  const api = useApiService();
  const [concept, setConcept] = useState<Concept | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadConcept = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<Concept>(`/concepts/${conceptId}`);
      setConcept(data);
      setError(null);
    } catch (err) {
      console.error("Failed to load concept", err);
      setError(err instanceof Error ? err.message : "Unable to load concept");
    } finally {
      setLoading(false);
    }
  }, [api, conceptId]);

  useEffect(() => {
    void loadConcept();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadConcept]);

  const handleDelete = async () => {
    if (!concept) return;

    setDeleting(true);
    try {
      await api.delete(`/concepts/${concept.id}`);
      toast.success("Concept deleted", {
        description: "The concept and all supporting metadata were removed.",
      });
      onNavigate("concept-dashboard");
    } catch (err) {
      console.error("Failed to delete concept", err);
      toast.error("Unable to delete concept", {
        description: err instanceof Error ? err.message : "Please try again later.",
      });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-muted-foreground gap-3">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <span className="text-sm">Loading concept detail…</span>
      </div>
    );
  }

  if (error || !concept) {
    return (
      <Card className="mx-auto max-w-3xl">
        <CardHeader>
          <CardTitle className="text-xl">We couldn't load this concept</CardTitle>
          <CardDescription>{error ?? "The concept may have been deleted."}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <Button variant="outline" onClick={() => onNavigate("concept-dashboard")}>Back to concepts</Button>
          <Button onClick={() => void loadConcept()}>Try again</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
              onClick={() => onNavigate("concept-dashboard")}
            >
              <ArrowLeft className="h-4 w-4" />
              Back to concepts
            </Button>
            <Badge variant="secondary" className="capitalize">
              {concept.decisionStage || "Unspecified stage"}
            </Badge>
          </div>
          <h1 className="text-3xl font-bold text-foreground">{concept.title}</h1>
          <p className="text-muted-foreground max-w-3xl">
            {concept.coreValueProposition || "No core value proposition has been captured yet."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <CapabilityButton capability={P.CampaignConceptUpdate}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
            onClick={() => onNavigate("edit-concept", undefined, concept.id)}
          >
            <Pencil className="h-4 w-4" />
            Edit concept
          </CapabilityButton>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <CapabilityButton capability={P.CampaignConceptDelete}
                variant="destructive"
                size="sm"
                className="flex items-center gap-2"
                disabled={deleting}
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                Delete
              </CapabilityButton>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete concept</AlertDialogTitle>
                <AlertDialogDescription>
                  This action will permanently remove "{concept.title}". You will lose all strategic, customer, and technical metadata.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <CapabilityButton capability={P.CampaignConceptDelete} component={AlertDialogAction}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={() => void handleDelete()}
                  disabled={deleting}
                >
                  Delete concept
                </CapabilityButton>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Product type</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-semibold text-foreground">{concept.productType || "—"}</p>
            <p className="text-xs text-muted-foreground mt-1">Last updated {formatDateTime(concept.updatedAt)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Target audience</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-foreground whitespace-pre-line">{concept.targetAudience || "—"}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Lifecycle</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <DetailRow label="Created" value={formatDateTime(concept.createdAt)} />
            <DetailRow label="Decision stage" value={concept.decisionStage || "Unspecified"} />
            <DetailRow label="Test status" value={concept.testStatus} />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Product & Concept Basics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <DetailRow label="Consumption occasion" value={concept.consumptionOccasion} />
            <DetailRow label="Core value proposition" value={concept.coreValueProposition} />
            <DetailRow label="Marketing objective" value={concept.marketingObjective} />
            <DetailRow label="Fit with brand strategy" value={concept.brandFit} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Strategic Alignment</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <DetailRow label="Target markets" value={concept.targetMarkets} />
            <DetailRow label="Positioning statement" value={concept.positioningStatement} />
            <DetailRow label="Competitive set / benchmarks" value={concept.competitiveSet} />
            <DetailRow label="Value for customer" value={concept.valueForCustomer} />
            <DetailRow label="Expected willingness to pay / price positioning" value={concept.pricePositioning} />
            <DetailRow label="Distribution channel" value={concept.distributionChannel} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Customer & Market Insights</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <DetailRow label="Customer insights" value={concept.customerInsights} />
            <DetailRow label="Trends addressed" value={concept.trendsAddressed} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Execution Plan</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <DetailRow label="Packaging concept" value={concept.packagingConcept} />
            <DetailRow label="Format / SKU" value={concept.formatSku} />
            <DetailRow label="Marketing mix" value={concept.marketingMix} />
            <DetailRow label="Launch timing" value={concept.launchTiming} />
            <DetailRow label="Regulatory / compliance requirements" value={concept.regulatoryRequirements} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Evaluation & Risk</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <DetailRow label="Success metrics" value={concept.successMetrics} />
            <DetailRow label="Business case assumptions" value={concept.businessCaseAssumptions} />
            <DetailRow label="Risks / barriers" value={concept.risks} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Technical Foundations</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <DetailRow label="Recipe / ingredient highlights" value={concept.recipeHighlights} />
            <DetailRow label="Shelf life / storage conditions" value={concept.shelfLife} />
            <DetailRow label="Production feasibility" value={concept.productionFeasibility} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
